import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { WebModal } from '@components/Modals/WebModal';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  GetChatDocument,
  ListMessagesDocument,
  useUpdateUserRequestStatusMutation,
  useListPendingUserRequestsQuery,
  UserRequestCategory,
  UserRequestable,
} from '@graphql/generated';

type ChatGroupRequestModalProps = {
  chatId: string;
  onClose: () => void;
  visible: boolean;
};
export const ChatGroupRequestModal: React.FC<ChatGroupRequestModalProps> = ({
  chatId,
  onClose,
  visible,
}) => {
  const REQUEST_ROW_HEIGHT = 64;
  const MAX_VISIBLE_REQUEST_ROWS = 5;
  const { t } = useTranslation('models');

  const requestAttributes = {
    requestType: UserRequestCategory.MemberAddition,
    userRequestableId: chatId,
    userRequestableType: UserRequestable.Chat,
  };
  const { data: pendingRequestData, loading } = useListPendingUserRequestsQuery(
    {
      variables: {
        attributes: requestAttributes,
      },
      skip: !chatId,
    }
  );

  const pendingRequest =
    pendingRequestData?.listPendingUserRequests?.filter(
      (u) => u.status === 'pending' && u.canCurrentUserDecide === true
    ) ?? [];
  const [updateUserRequestStatus] = useUpdateUserRequestStatusMutation();

  useEffect(() => {
    if (!loading && pendingRequest.length < 1) onClose();
  }, [loading, onClose, pendingRequest.length]);

  const onAcceptOrDeclineRequest = (id: string, isAccepted: boolean) => {
    const shouldClose = pendingRequest.length <= 1;
    updateUserRequestStatus({
      variables: {
        id,
        status: isAccepted ? 'approved' : 'rejected',
      },
      onCompleted: () => {
        if (shouldClose) onClose();
      },
      update: (cache, { data }) => {
        const updated = data?.updateUserRequestStatus;
        if (!updated?.id) return;
        cache.evict({
          id: cache.identify({ __typename: 'UserRequest', id: updated.id }),
        });
        cache.gc();
      },
      refetchQueries: isAccepted
        ? [
            { query: GetChatDocument, variables: { id: chatId } },
            { query: ListMessagesDocument, variables: { chatId } },
          ]
        : [{ query: GetChatDocument, variables: { id: chatId } }],
    });
  };
  return (
    <WebModal
      accessibilityLabel='Chat Group Request Modal'
      onClose={onClose}
      visible={visible}
      width={504}
      height={
        80 +
        Math.min(
          REQUEST_ROW_HEIGHT * (pendingRequest.length ?? 1),
          REQUEST_ROW_HEIGHT * MAX_VISIBLE_REQUEST_ROWS
        )
      }>
      <Box mx='m' my='l'>
        <Box flexDirection='row'>
          <Text
            flex={1}
            mx='xs'
            variant='labelLarge'
            numberOfLines={1}
            ellipsizeMode='tail'
            flexShrink={1}
            color='black'>
            {t('models:chat.youHaveRequests', {
              requestCount: pendingRequest.length,
            })}
          </Text>
          <Icon
            marginLeft='m'
            name='Close'
            variant='xs'
            color='textPrimary'
            onPress={() => {
              onClose();
            }}
          />
        </Box>
        {(pendingRequest.length ?? 0) > 0 && (
          <ScrollView
            showsVerticalScrollIndicator={true}
            style={{
              maxHeight: REQUEST_ROW_HEIGHT * MAX_VISIBLE_REQUEST_ROWS,
            }}>
            {pendingRequest.map((cu) => {
              return (
                <Box
                  key={cu.id}
                  mt='l'
                  flexDirection='row'
                  justifyContent='center'
                  alignItems='center'>
                  <Box flexDirection='row' justifyContent='center'>
                    <Avatar
                      size='medium'
                      id={cu.id}
                      avatarType='user'
                      isMultiple={false}
                      disableAvatarPress={false}
                      avatar={cu?.user?.avatar}
                      label={cu?.user?.name ?? t('shared:unknownUser')}
                    />
                  </Box>

                  <Text
                    flex={1}
                    mx='xs'
                    variant='body'
                    numberOfLines={1}
                    ellipsizeMode='tail'
                    color='textPrimary'>
                    {cu?.user?.name ?? t('shared:unknownUser')}
                  </Text>

                  <Button
                    padding='none'
                    isSmall={true}
                    variant='edit'
                    borderColor='black'
                    borderRadius='xs'
                    borderWidth={1}
                    disabled={false}
                    height={32}
                    width={96}
                    backgroundColor='white'
                    onPress={() => {
                      onAcceptOrDeclineRequest(cu.id, false);
                    }}
                    accessibilityLabel={t('shared:decline')}>
                    <Text variant='labelSmall' color='black'>
                      {t('shared:decline')}
                    </Text>
                  </Button>
                  <Button
                    marginLeft='s'
                    padding='none'
                    isSmall={true}
                    variant='edit'
                    borderColor={'#18A87D' as any}
                    borderRadius='xs'
                    borderWidth={1}
                    disabled={false}
                    height={32}
                    width={96}
                    backgroundColor='white'
                    onPress={() => {
                      onAcceptOrDeclineRequest(cu.id, true);
                    }}
                    accessibilityLabel={t('shared:accept')}>
                    <Text variant='labelSmall' color='greenSecondary'>
                      {t('shared:accept')}
                    </Text>
                  </Button>
                </Box>
              );
            })}
          </ScrollView>
        )}
      </Box>
    </WebModal>
  );
};
